import { useSyncExternalStore } from 'react';
import { bumpChartMask } from '@vitness/charts';
import { setMaskFlag } from './format';

export const MASK_KEY = 'vudget-masked';

/**
 * App-wide "hide amounts" — the finance-app privacy toggle, Robinhood style.
 *
 * Amounts are *substituted* with `$••••`, not blurred. The substitution
 * happens in lib/format (every dollar figure flows through fmtMoney /
 * compactMoneyFmt / <Money>), which is what makes the mask complete and only
 * complete: figures mask everywhere without opt-in, while sentences that
 * merely mention money stay readable. The first blur-based version got this
 * exactly backwards — it hid whatever happened to carry a `.vd-num` class,
 * which blurred captions and missed any amount styled inline.
 *
 * Charts keep a light CSS blur (`[data-masked] canvas`): their figures are
 * painted, not DOM, so substitution can't reach them — but the blur is mild
 * enough that the *shape* stays readable, which is the part that was never a
 * secret.
 *
 * Implemented as a tiny external store so the shell toggle and the whole tree
 * observe the same value; every subscriber re-renders on toggle, which is
 * what re-runs the format calls. localStorage only, deliberately not synced
 * to D1: whether the phone masks on the train says nothing about what the
 * laptop at home should do.
 */
let masked = (() => {
  try { return localStorage.getItem(MASK_KEY) === '1'; } catch { return false; }
})();

const listeners = new Set<() => void>();

export function applyMask(on: boolean): void {
  setMaskFlag(on);
  bumpChartMask();   // painted figures repaint on the next render
  if (typeof document !== 'undefined') {
    if (on) document.documentElement.setAttribute('data-masked', '');
    else document.documentElement.removeAttribute('data-masked');
  }
}

// The format flag must be right before anything renders.
applyMask(masked);

export function toggleMask(): void {
  masked = !masked;
  try { localStorage.setItem(MASK_KEY, masked ? '1' : '0'); } catch { /* private mode */ }
  applyMask(masked);
  listeners.forEach(l => l());
}

const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };
const getSnapshot = () => masked;

export function useMasked() {
  const value = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return { masked: value, toggle: toggleMask };
}
